// Pure rules behind /backoffice/orders-new — the BO order list drawn like
// /seller/orders (status tabs + right-hand filter panel).
// Spec: docs/specs/2026-10-07-bo-orders-new-status-tabs-design.md §3, §6.6.
//
// No vue import: this repo's jest cannot mount a .vue file
// (reference-bo-fn-jest-infra), so OrdersNew.vue only renders what lives here
// and the behaviour is pinned by tests/unit/ordersNew.spec.js.
//
// --------------------------------------------------------------------------
// WHY THE TABS ARE COUNTED HERE (spec §2 items 1, 5)
//
// GET /api/v1/orders has no server paging and returns a bare array (capped at
// 2000 rows, 100000 for admins with a date range). The page fetches ONCE with
// no `order_status[]` — every status in the window — and splits it into tabs,
// counts and pages on the client. A tab count is therefore the number of rows
// the tab shows, by definition, and switching tabs never calls the API.
//
// `today` (YYYY-MM-DD) is always passed in so every function stays pure.
//
// ROUND 2 (§10): one fetch PER PLATFORM (ruling R8) — the BE row cap counts
// per request, so a single all-platform fetch would let the big marketplaces
// eat the cap. The page keeps `rowsByPlatform = { "<id>": DBOrder[] | null }`
// (null = that request failed) and draws a platform x tab count table
// (`platformRows`); a cell click is still client-only.
// --------------------------------------------------------------------------

import { canDeleteOrder } from './orderDelete';

export const TAB_KEYS = ['all', 'unpaid', 'to_ship', 'shipping', 'completed', 'cancelled', 'returns'];

/** Ruling R1 (§9): BO search is mostly lookups across every status. */
export const DEFAULT_TAB = 'all';

/** §3. Same groups as the seller page plus `unpaid` (100 is a real BO queue). */
export const TAB_STATUSES = {
  all: [],
  unpaid: [100],
  to_ship: [111, 112, 115],
  shipping: [114, 121],
  completed: [122, 130],
  cancelled: [140],
  returns: [143, 141, 142]
};

/** Every known code, in tab order — the sub-status radios of the `all` tab. */
export const ALL_STATUS_CODES = [100, 111, 112, 115, 114, 121, 122, 130, 140, 143, 141, 142];

/** Status text keys, the same ones OrderTables.vue draws (top-level i18n keys). */
export const STATUS_LABEL_KEYS = {
  100: 'order_status_unpaid',
  111: 'order_status_awaiting_shipment',
  112: 'order_status_awaitingcollection',
  114: 'order_status_partiallyshipping',
  115: 'order_status_awaiting_transit',
  121: 'order_status_intransit',
  122: 'order_status_delivered',
  130: 'order_status_completed',
  140: 'order_status_cancelled',
  141: 'order_status_returned',
  142: 'order_status_claimed',
  143: 'order_status_returning'
};

// --------------------------------------------------------------------------
// ROUND 11 (§19 R45/R46): fulfilment stages — five extra count columns after
// the tab columns, split by status + the packing scan timestamps that the
// list rows already carry. A stage cell selects its tab plus a client-side
// `stage` filter (URL `?stage=`); no extra API call.
// --------------------------------------------------------------------------

/** Column order of the stage columns. */
export const STAGE_KEYS = ['wait_cutoff', 'wait_open', 'packing', 'wait_pickup', 'in_transit'];

/** The tab a stage lives in (its status is in that tab's TAB_STATUSES). */
export const STAGE_TAB = {
  wait_cutoff: 'to_ship',
  wait_open: 'to_ship',
  packing: 'to_ship',
  wait_pickup: 'to_ship',
  in_transit: 'shipping'
};

/** The one status code of each stage (112 is split by the packing scans). */
export const STAGE_STATUS = {
  wait_cutoff: 111,
  wait_open: 112,
  packing: 112,
  wait_pickup: 115,
  in_transit: 121
};

export function isStageKey(key) {
  return STAGE_KEYS.indexOf(key) >= 0;
}

/**
 * A packing timestamp is "set" when it is a non-blank string that is not a
 * zero date (Go zero time / MySQL zero date). null / undefined / '' / '   ' /
 * anything that is not a string or Date = not set.
 */
export function hasTimestamp(value) {
  if (value instanceof Date) return !Number.isNaN(value.getTime());
  if (typeof value !== 'string') return false;
  const s = value.trim();
  if (!s) return false;
  return !/^(0001-01-01|0000-00-00)/.test(s);
}

/**
 * fulfilmentStage(row) -> one of STAGE_KEYS, or '' when the row is in none:
 *   111 -> wait_cutoff · 112 without packing_opened_at -> wait_open ·
 *   112 opened and not closed -> packing · 115 -> wait_pickup · 121 -> in_transit.
 * A 112 that is already closed (status not yet moved to 115) is in none.
 * Junk / unknown status -> ''. Never throws.
 */
export function fulfilmentStage(row) {
  if (!row || typeof row !== 'object') return '';
  const raw = row.order_status;
  if (raw === null || raw === undefined || raw === '') return '';
  const code = Number(raw);
  if (code === 111) return 'wait_cutoff';
  if (code === 115) return 'wait_pickup';
  if (code === 121) return 'in_transit';
  if (code !== 112) return '';
  if (!hasTimestamp(row.packing_opened_at)) return 'wait_open';
  if (!hasTimestamp(row.packing_closed_at)) return 'packing';
  return '';
}

/** { wait_cutoff: n, ... } for a list of rows (non-arrays count as empty). */
export function countStages(rows) {
  const out = {};
  STAGE_KEYS.forEach(k => {
    out[k] = 0;
  });
  (Array.isArray(rows) ? rows : []).forEach(row => {
    const k = fulfilmentStage(row);
    if (k) out[k] += 1;
  });
  return out;
}

/** Rows in one stage; '' / junk stage = the rows unchanged. Never mutates. */
export function rowsForStage(rows, stage) {
  const list = Array.isArray(rows) ? rows : [];
  if (!isStageKey(stage)) return list;
  return list.filter(row => fulfilmentStage(row) === stage);
}

/** A stage from the URL / a click: one of STAGE_KEYS, anything else -> ''. */
export function normalizeStage(value) {
  const s = cleanText(value);
  return isStageKey(s) ? s : '';
}

/** BE `limitRow` values (BO-Orders.go): a result this long is probably cut. */
export const ROW_CAPS = [2000, 100000];

/** BE rejects a window longer than this unless a keyword lookup is sent. */
export const MAX_RANGE_DAYS = 35;

/** §10.2: 10 rows per page by default, the owner picks 10/20/50/100. */
export const PAGE_SIZE = 10;
export const PAGE_SIZES = [10, 20, 50, 100];

/** §10.2 ruling R11: the page opens on the ทั้งหมด (every platform) row. */
export const DEFAULT_PLATFORM = 'all';

export const DATE_TYPES = [
  { value: 'created', labelKey: 'orders_new.date_created' },
  { value: 'payment', labelKey: 'orders_new.date_payment' }
];
export const DEFAULT_DATE_TYPE = 'created';

/** `packing_not_close` values (OrderTables PackingStatusTable). */
export const PACKING_OPTIONS = [
  { value: 0, labelKey: 'orders_new.packing_all' },
  { value: 1, labelKey: 'orders_new.packing_closed' },
  { value: 2, labelKey: 'orders_new.packing_open' },
  { value: 3, labelKey: 'orders_new.packing_not_opened' }
];

/** Platforms whose own default view = MadameFin + MDFStore (0) or themselves (1, 9). */
const SELF_PLATFORM_IDS = [0, 1, 9];

const ID_FIELDS = ['bo_user_id', 'bo_platform_id', 'bo_sale_channel_id', 'bo_sale_channel_type_id', 'payment_method_type'];

const MAX_TEXT_LEN = 100;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 24 * 60 * 60 * 1000;

const KEYWORD_KINDS = {
  phone: 'recipient_phone',
  order: 'order_number',
  tracking: 'tracking_number',
  name: 'recipient_name'
};

function first(value) {
  return Array.isArray(value) ? value[0] : value;
}

function cleanText(value) {
  const v = first(value);
  if (v === undefined || v === null) return '';
  return String(v).trim().slice(0, MAX_TEXT_LEN);
}

function toId(value) {
  const s = cleanText(value);
  if (!/^\d{1,12}$/.test(s)) return 0;
  return Number(s);
}

/** 'YYYY-MM-DD' -> UTC ms, NaN when not a real calendar date. */
function dayMs(s) {
  if (!DATE_RE.test(s || '')) return NaN;
  const [y, m, d] = s.split('-').map(Number);
  const ms = Date.UTC(y, m - 1, d);
  const back = new Date(ms);
  if (back.getUTCFullYear() !== y || back.getUTCMonth() !== m - 1 || back.getUTCDate() !== d) return NaN;
  return ms;
}

function cleanDate(value) {
  const s = cleanText(value);
  return Number.isNaN(dayMs(s)) ? '' : s;
}

function fmtDay(ms) {
  const d = new Date(ms);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

export function isTabKey(key) {
  return TAB_KEYS.indexOf(key) >= 0;
}

/** The tab a status code lives in; '' for an unknown code (only `all` shows it). */
export function tabOfStatus(code) {
  if (code === null || code === undefined || code === '') return '';
  const n = Number(code);
  const hit = TAB_KEYS.find(k => k !== 'all' && TAB_STATUSES[k].indexOf(n) >= 0);
  return hit || '';
}

/** The sub-status radios the panel offers on a tab (§6.5 field 1). */
export function subStatusOptions(tab) {
  if (tab === 'all' || !isTabKey(tab)) return ALL_STATUS_CODES.slice();
  return TAB_STATUSES[tab].slice();
}

const STATUS_BADGE = {
  unpaid: 'badge-primary',
  to_ship: 'badge-warning',
  shipping: 'badge-info',
  completed: 'badge-success',
  cancelled: 'badge-danger',
  returns: 'badge-dark'
};

/** Badge colour by the tab the code belongs to; secondary for unknown codes. */
export function statusBadgeClass(code) {
  return STATUS_BADGE[tabOfStatus(code)] || 'badge-secondary';
}

/**
 * orderStatusExtras — the small labels after a row's status badge (spec
 * 2026-10-07-shopee-cancel-reconcile-design.md §11 F1). Shopee rows only
 * (platform_id 3):
 * - cancelRequested: Shopee says IN_CANCEL and BO is not 140 -> orange
 *   "ขอยกเลิก (รอตอบ)" (the buyer's request is still open at Shopee);
 * - staleChip: BO is 140 but the stored Shopee status is something else than
 *   CANCELLED -> that status as a grey chip (it is the LAST value we heard and
 *   may be older than Shopee's real one), null otherwise.
 */
export function orderStatusExtras(row) {
  const r = row && typeof row === 'object' ? row : {};
  const none = { cancelRequested: false, staleChip: null };
  if (Number(r.platform_id) !== 3) return none;
  const ps = typeof r.platform_order_status === 'string' ? r.platform_order_status.trim() : '';
  const code = Number(r.order_status);
  return {
    cancelRequested: ps.toUpperCase() === 'IN_CANCEL' && code !== 140,
    staleChip: code === 140 && ps !== '' && ps.toUpperCase() !== 'CANCELLED' ? ps : null
  };
}

/**
 * countTabs — counts for every tab plus `other` (unknown codes, seen only in
 * `all`), and a count per status code. all = sum of the six + other (§3).
 */
export function countTabs(rows) {
  const tabCounts = { all: 0, other: 0 };
  TAB_KEYS.forEach(k => {
    tabCounts[k] = 0;
  });
  const statusCounts = {};
  (Array.isArray(rows) ? rows : []).forEach(row => {
    const code = row ? row.order_status : undefined;
    tabCounts.all += 1;
    const tab = tabOfStatus(code);
    if (tab) tabCounts[tab] += 1;
    else tabCounts.other += 1;
    if (code !== undefined && code !== null && code !== '') {
      const key = String(Number(code));
      statusCounts[key] = (statusCounts[key] || 0) + 1;
    }
  });
  return { tabCounts, statusCounts };
}

/** Rows of one tab, narrowed by a sub-status when set. Never mutates. */
export function rowsFor(rows, tab, status) {
  const list = Array.isArray(rows) ? rows : [];
  const t = isTabKey(tab) ? tab : DEFAULT_TAB;
  const s = status === undefined || status === null || status === '' ? null : Number(status);
  return list.filter(row => {
    const code = row ? Number(row.order_status) : NaN;
    if (t !== 'all' && TAB_STATUSES[t].indexOf(code) < 0) return false;
    if (s !== null && code !== s) return false;
    return true;
  });
}

/** The last valid page for `count` rows (1 when empty). */
export function lastPage(count, size) {
  const sz = Math.max(1, Math.floor(Number(size)) || PAGE_SIZE);
  return Math.max(1, Math.ceil((Number(count) || 0) / sz));
}

/** One client page. A page past the end is clamped to the last page. */
export function pageOf(rows, page, size) {
  const list = Array.isArray(rows) ? rows : [];
  const sz = Math.max(1, Math.floor(Number(size)) || PAGE_SIZE);
  const p = Math.min(Math.max(1, Math.floor(Number(page)) || 1), lastPage(list.length, sz));
  return list.slice((p - 1) * sz, p * sz);
}

/** True when the result hit a BE row cap (edge 3). */
export function isTruncated(rows) {
  return Array.isArray(rows) && ROW_CAPS.indexOf(rows.length) >= 0;
}

/**
 * classifyKeyword — which lookup param the free-text box becomes.
 * Port of OrderTables.vue keywordTypeID without the `g` flag (a `g` regex
 * keeps lastIndex between .test() calls and alternates true/false).
 * A marketplace platform (not 0/1/9) makes every keyword a tracking number.
 */
export function classifyKeyword(keyword, platformId) {
  const kw = cleanText(keyword);
  if (!kw) return { kind: '', params: {} };
  let kind;
  if (SELF_PLATFORM_IDS.indexOf(toId(platformId)) < 0) {
    kind = 'tracking';
  } else if (/^[0]\d{9}$/.test(kw)) {
    kind = 'phone';
  } else if (/MDF\d{1,12}$/i.test(kw) || /^\d{1,12}$/.test(kw)) {
    kind = 'order';
  } else if (
    /^[A-Z]{2}\d{9}[A-Z]{2}$/i.test(kw) || // Thailand Post
    /^[A-Z]{4}\d{9}$/i.test(kw) || // Kerry
    /^[A-Z]{5}\d{10}$/i.test(kw) || // Kerry (Lazada) / DHL
    /^SD[A-Z]{2}\d{10}$/i.test(kw) || // Kerry (Shopee)
    /^TH[0-9A-Z]{12,13}$/i.test(kw) || // Flash
    /^[0-9]{12}$/i.test(kw) || // J&T
    /^SPX[0-9A-Z]{12,14}$/i.test(kw) // SPX
  ) {
    kind = 'tracking';
  } else {
    kind = 'name';
  }
  const value = kind === 'order' ? kw.toUpperCase().replace('MDF', '') : kw;
  return { kind, params: { [KEYWORD_KINDS[kind]]: value } };
}

/** Default window = today-2 .. today (OrderTables.vue mounted). */
export function defaultRange(today) {
  const t = dayMs(cleanText(today));
  if (Number.isNaN(t)) return { time_from: '', time_to: '' };
  return { time_from: fmtDay(t - 2 * DAY_MS), time_to: fmtDay(t) };
}

export function emptyFilters(today) {
  const r = defaultRange(today);
  return {
    status: '',
    date_type: DEFAULT_DATE_TYPE,
    time_from: r.time_from,
    time_to: r.time_to,
    q: '',
    bo_user_id: 0,
    bo_platform_id: 0,
    bo_sale_channel_id: 0,
    bo_sale_channel_type_id: 0,
    payment_method_type: 0,
    packing_not_close: 0
  };
}

/**
 * normalizeFilters — any loose object (draft, URL query) -> the model.
 * Junk is dropped, never thrown (edge 5): an unknown status, date type or
 * packing value falls back to its default; a missing / broken date takes the
 * default window's day.
 */
export function normalizeFilters(obj, today) {
  const src = obj && typeof obj === 'object' ? obj : {};
  const f = emptyFilters(today);
  const status = cleanText(src.status);
  if (/^\d{1,4}$/.test(status) && ALL_STATUS_CODES.indexOf(Number(status)) >= 0) f.status = String(Number(status));
  const dt = cleanText(src.date_type);
  if (DATE_TYPES.some(d => d.value === dt)) f.date_type = dt;
  const from = cleanDate(src.time_from);
  const to = cleanDate(src.time_to);
  if (from) f.time_from = from;
  if (to) f.time_to = to;
  f.q = cleanText(src.q);
  ID_FIELDS.forEach(k => {
    f[k] = toId(src[k]);
  });
  const packing = toId(src.packing_not_close);
  f.packing_not_close = PACKING_OPTIONS.some(p => p.value === packing) ? packing : 0;
  return f;
}

/**
 * rangeError — '' when the window can be sent; 'inverted' when from > to;
 * 'too_long' past MAX_RANGE_DAYS (35 passes, 36 fails — BO-Orders.go diffDay > 35).
 * A keyword makes the BE skip the dates, so it always passes (edge 2).
 */
export function rangeError(filters) {
  const f = filters || {};
  if (cleanText(f.q)) return '';
  const from = dayMs(cleanText(f.time_from));
  const to = dayMs(cleanText(f.time_to));
  if (Number.isNaN(from) || Number.isNaN(to)) return 'too_long';
  if (from > to) return 'inverted';
  if ((to - from) / DAY_MS > MAX_RANGE_DAYS) return 'too_long';
  return '';
}

/** Rows per page: one of PAGE_SIZES, anything else -> PAGE_SIZE (§10.2). */
export function normalizeSize(value) {
  const n = toId(value);
  return PAGE_SIZES.indexOf(n) >= 0 ? n : PAGE_SIZE;
}

/** 'all' | '<id>' without a list to check against (the URL shape only). */
function platformShape(value) {
  const id = toId(value);
  return id > 0 ? String(id) : DEFAULT_PLATFORM;
}

/**
 * normalizePlatform — the selected row: 'all' or a '<id>' that is in `ids`.
 * An id not in the list (?platform=999) or junk -> 'all' (acceptance 17).
 */
export function normalizePlatform(value, ids) {
  const id = toId(value);
  if (!id) return DEFAULT_PLATFORM;
  const list = Array.isArray(ids) ? ids.map(Number) : [];
  return list.indexOf(id) >= 0 ? String(id) : DEFAULT_PLATFORM;
}

/**
 * `$route.query` -> { platform, tab, stage, filters, page, size }. `platform`
 * is only shape-checked here ('all' | '<id>'); the view runs normalizePlatform
 * with the platform list. `stage` is one of STAGE_KEYS or '' (junk dropped);
 * a valid stage overrides `tab` with STAGE_TAB[stage] (§19 R46).
 */
export function filtersFromQuery(query, today) {
  const q = query && typeof query === 'object' ? query : {};
  const t = cleanText(q.tab);
  const stage = normalizeStage(q.stage);
  // §19 R46: a stage implies its tab (?stage=in_transit&tab=to_ship -> shipping).
  const tab = stage ? STAGE_TAB[stage] : isTabKey(t) ? t : DEFAULT_TAB;
  const page = Math.max(1, toId(q.page) || 1);
  return {
    platform: platformShape(q.platform),
    tab,
    stage,
    filters: normalizeFilters(q, today),
    page,
    size: normalizeSize(q.size)
  };
}

/**
 * { tab, filters, page } (+ opts { platform, size, stage }) -> `$route.query`.
 * Defaults (incl. the default window, platform 'all', size 10, no stage) are
 * left out; a valid stage writes its own STAGE_TAB[stage] as `tab`.
 * Without `opts` the result is the round-1 query.
 */
export function queryFromFilters(tab, filters, page, today, opts) {
  const f = normalizeFilters(filters, today);
  const def = emptyFilters(today);
  const o = opts && typeof opts === 'object' ? opts : {};
  const out = {};
  const platform = platformShape(o.platform);
  if (platform !== DEFAULT_PLATFORM) out.platform = platform;
  // §19 R46: a valid stage always wins — it is written with its own tab
  // (symmetric with filtersFromQuery), so the URL never drops a picked stage.
  const stage = normalizeStage(o.stage);
  const outTab = stage ? STAGE_TAB[stage] : tab;
  if (isTabKey(outTab) && outTab !== DEFAULT_TAB) out.tab = outTab;
  if (stage) out.stage = stage;
  const p = toId(page);
  if (p > 1) out.page = String(p);
  const size = normalizeSize(o.size);
  if (size !== PAGE_SIZE) out.size = String(size);
  if (f.status) out.status = f.status;
  if (f.date_type !== DEFAULT_DATE_TYPE) out.date_type = f.date_type;
  if (f.time_from !== def.time_from || f.time_to !== def.time_to) {
    out.time_from = f.time_from;
    out.time_to = f.time_to;
  }
  if (f.q) out.q = f.q;
  ID_FIELDS.forEach(k => {
    if (f[k]) out[k] = String(f[k]);
  });
  if (f.packing_not_close) out.packing_not_close = String(f.packing_not_close);
  return out;
}

/**
 * apiParams — GET /api/v1/orders params (§5). Never `order_status` (every
 * status in the window is fetched and split on the client), never
 * `platform_order_status` / `bo_packing_id` / `filter_date`. Only values that
 * differ from 0 / '' are sent; the window is always sent (the BE requires it).
 */
export function apiParams(filters) {
  const f = filters || {};
  const out = {
    time_from: cleanText(f.time_from),
    time_to: cleanText(f.time_to)
  };
  if (cleanText(f.date_type) === 'payment') out.date_type = 'payment';
  Object.assign(out, classifyKeyword(f.q, f.bo_platform_id).params);
  ID_FIELDS.forEach(k => {
    const v = toId(f[k]);
    if (v) out[k] = v;
  });
  const packing = toId(f.packing_not_close);
  if (packing) out.packing_not_close = packing;
  return out;
}

/** Platform ids to fetch: id > 0, first occurrence, list order (§10.1). */
export function platformIds(platformList) {
  if (!Array.isArray(platformList)) return [];
  const out = [];
  platformList.forEach(p => {
    const id = p && typeof p === 'object' ? toId(p.id) : 0;
    if (id > 0 && out.indexOf(id) < 0) out.push(id);
  });
  return out;
}

/** apiParams for one platform's request; the keyword is classified for that platform. */
export function platformApiParams(filters, platformId) {
  const f = filters && typeof filters === 'object' ? filters : {};
  return apiParams(Object.assign({}, f, { bo_platform_id: platformId }));
}

function createdKey(row) {
  const v = row ? row.created_at : '';
  return v === undefined || v === null ? '' : String(v);
}

function idKey(row) {
  const n = Number(row ? row.ID : NaN);
  return Number.isNaN(n) ? -Infinity : n;
}

/**
 * mergeRows — every platform's rows in one list (failed = null skipped),
 * newest first: created_at DESC, then ID DESC. Never mutates the input.
 */
export function mergeRows(rowsByPlatform) {
  const src = rowsByPlatform && typeof rowsByPlatform === 'object' ? rowsByPlatform : {};
  let all = [];
  Object.keys(src).forEach(k => {
    if (Array.isArray(src[k])) all = all.concat(src[k]);
  });
  return all.sort((a, b) => {
    const ca = createdKey(a);
    const cb = createdKey(b);
    if (ca !== cb) return ca < cb ? 1 : -1;
    return idKey(b) - idKey(a);
  });
}

/** Rows of a selected platform: 'all' = mergeRows, '<id>' = that list (failed -> []). */
export function rowsOfPlatform(rowsByPlatform, platform) {
  const src = rowsByPlatform && typeof rowsByPlatform === 'object' ? rowsByPlatform : {};
  const id = toId(platform);
  if (!id) return mergeRows(src);
  const rows = src[String(id)];
  return Array.isArray(rows) ? rows : [];
}

/**
 * platformRows — the count table (§10.2, §10.3): one row per platformIds
 * entry (label = list `name`), then the ทั้งหมด row last.
 *   { key: '<id>' | 'all', label, failed, truncated, counts, tabs,
 *     stageCounts, stages }
 * `stages` (§19 R45) = [{ key, count }] in STAGE_KEYS order, counted by
 * fulfilmentStage; null counts on a failed row, summed like `tabs`.
 * A platform is `failed` when its entry is not an array (null = the request
 * failed): its cells are null and it is left out of the ทั้งหมด sums. The
 * ทั้งหมด row fails only when every platform failed; it is truncated when any
 * platform is. Counts are before the sub-status (§3), so cell (p, k) =
 * rowsFor(rowsOfPlatform(p), k, '').length.
 */
export function platformRows(rowsByPlatform, platformList, t) {
  const src = rowsByPlatform && typeof rowsByPlatform === 'object' ? rowsByPlatform : {};
  const list = Array.isArray(platformList) ? platformList : [];
  const tabsOf = (counts, failed) => TAB_KEYS.map(k => ({ key: k, count: failed ? null : counts[k] }));
  const stagesOf = (counts, failed) => STAGE_KEYS.map(k => ({ key: k, count: failed ? null : counts[k] }));
  const out = platformIds(list).map(id => {
    const hit = list.find(p => p && toId(p.id) === id);
    const rows = src[String(id)];
    const failed = !Array.isArray(rows);
    const counts = countTabs(failed ? [] : rows).tabCounts;
    const stageCounts = countStages(failed ? [] : rows);
    return {
      key: String(id),
      label: hit && hit.name !== undefined && hit.name !== null ? String(hit.name) : String(id),
      failed,
      truncated: !failed && isTruncated(rows),
      counts,
      tabs: tabsOf(counts, failed),
      stageCounts,
      stages: stagesOf(stageCounts, failed)
    };
  });
  const sum = countTabs([]).tabCounts;
  const stageSum = countStages([]);
  out.forEach(r => {
    if (r.failed) return;
    Object.keys(sum).forEach(k => {
      sum[k] += r.counts[k];
    });
    STAGE_KEYS.forEach(k => {
      stageSum[k] += r.stageCounts[k];
    });
  });
  const allFailed = out.length > 0 && out.every(r => r.failed);
  const label = typeof t === 'function' ? t('orders_new.platform_all') : 'orders_new.platform_all';
  out.push({
    key: DEFAULT_PLATFORM,
    label: label === undefined || label === null ? 'orders_new.platform_all' : String(label),
    failed: allFailed,
    truncated: out.some(r => r.truncated),
    counts: sum,
    tabs: tabsOf(sum, allFailed),
    stageCounts: stageSum,
    stages: stagesOf(stageSum, allFailed)
  });
  return out;
}

function translate(labels, key) {
  const t = labels && typeof labels.t === 'function' ? labels.t : null;
  if (!t) return key;
  const s = t(key);
  return s === undefined || s === null ? key : String(s);
}

function lookup(labels, name, id) {
  const fn = labels && typeof labels[name] === 'function' ? labels[name] : null;
  const s = fn ? fn(id) : '';
  return s && s !== '-' ? String(s) : String(id);
}

/**
 * activeChips — one chip per field that differs from its default, in panel
 * order (§6.4 item 3, acceptance 7). The view renders
 * `$t(chip.labelKey): chip.text`.
 *
 * `labels` (all optional) = { t(key), user(id), platform(id), saleChannel(id),
 * saleChannelType(id), paymentType(id) } -> display strings.
 */
export function activeChips(filters, labels, today) {
  const f = normalizeFilters(filters, today);
  const def = emptyFilters(today);
  const chips = [];
  const push = (key, labelKey, text) => chips.push({ key, labelKey, text: String(text) });
  if (f.status) push('status', 'orders_new.f_sub_status', translate(labels, STATUS_LABEL_KEYS[f.status]));
  if (!showPeriodTag(f, today)) {
    const kind = translate(labels, DATE_TYPES.find(d => d.value === f.date_type).labelKey);
    push('date', 'orders_new.f_date', `${kind} ${f.time_from} – ${f.time_to}`);
  }
  if (f.q) push('q', `orders_new.kw_${classifyKeyword(f.q, f.bo_platform_id).kind}`, f.q);
  if (f.bo_user_id) push('bo_user_id', 'orders_new.f_user', lookup(labels, 'user', f.bo_user_id));
  if (f.bo_platform_id) push('bo_platform_id', 'orders_new.f_platform', lookup(labels, 'platform', f.bo_platform_id));
  if (f.bo_sale_channel_id) {
    push('bo_sale_channel_id', 'orders_new.f_sale_channel', lookup(labels, 'saleChannel', f.bo_sale_channel_id));
  }
  if (f.bo_sale_channel_type_id) {
    push(
      'bo_sale_channel_type_id',
      'orders_new.f_sale_channel_type',
      lookup(labels, 'saleChannelType', f.bo_sale_channel_type_id)
    );
  }
  if (f.payment_method_type) {
    push('payment_method_type', 'orders_new.f_payment_type', lookup(labels, 'paymentType', f.payment_method_type));
  }
  if (f.packing_not_close) {
    const opt = PACKING_OPTIONS.find(p => p.value === f.packing_not_close);
    push('packing_not_close', 'orders_new.f_packing', translate(labels, opt.labelKey));
  }
  return chips;
}

/**
 * showPeriodTag — the toolbar's "ช่วงเวลา: from – to" tag is shown only while
 * no date chip is (§11.3, R16): once the range or date type leaves its
 * default the closable chip carries the dates and the tag would repeat them.
 * Same condition as the 'date' chip in activeChips.
 */
export function showPeriodTag(filters, today) {
  const f = normalizeFilters(filters, today);
  const def = emptyFilters(today);
  return f.date_type === def.date_type && f.time_from === def.time_from && f.time_to === def.time_to;
}

/** The n in "ตัวกรอง (n)". */
export function activeCount(filters, today) {
  return activeChips(filters, null, today).length;
}

/** Filters with one chip's field back at its default. Never mutates the input. */
export function removeChip(filters, key, today) {
  const f = normalizeFilters(filters, today);
  const def = emptyFilters(today);
  if (key === 'date') {
    f.date_type = def.date_type;
    f.time_from = def.time_from;
    f.time_to = def.time_to;
  } else if (Object.prototype.hasOwnProperty.call(def, key)) {
    f[key] = def[key];
  }
  return f;
}

// --------------------------------------------------------------------------
// ROUND 4 (§12): the row ⋮ menu. Same set and order as the old page's row
// buttons (OrderTables.vue actions column) with the same hide rules; a
// failing condition HIDES the item (never disabled). `view` is always there,
// so the menu is never empty.
// --------------------------------------------------------------------------

export const ROW_ACTION_KEYS = ['view', 'shipping', 'tracking', 'sim_delivered', 'sim_failed', 'sim_returned', 'cancel'];

/** Actions whose modal lives in OrderTables.vue — handed off (ruling R18). */
export const HANDOFF_KINDS = ['shipping', 'tracking', 'cancel'];

/**
 * Old page: tracking edit for 112/115/121/130, admins only; + 140 (cancelled)
 * since bo-api #931 — admins may set or change it, the dialog warns.
 * 2026-10-15 owner: also 111/114/122.
 */
export const TRACKING_EDIT_STATUSES = [111, 112, 114, 115, 121, 122, 130, 140];

/** Old page `canSimulate` (#189, MDFStore + BE flag). */
export const SIMULATE_STATUSES = [111, 112, 114, 115, 121, 122];

const ROW_ACTION_META = {
  view: { labelKey: 'orders_new.view', icon: 'fa fa-eye' },
  shipping: { labelKey: 'orders_new.act_change_shipping', icon: 'fas fa-shipping-fast' },
  tracking: { labelKey: 'orders_new.act_change_tracking', icon: 'fa fa-truck' },
  sim_delivered: { labelKey: 'sim_delivered', icon: 'fa fa-check' },
  sim_failed: { labelKey: 'sim_failed', icon: 'fa fa-times' },
  sim_returned: { labelKey: 'sim_returned', icon: 'fa fa-undo' },
  cancel: { labelKey: 'orders_new.act_cancel', icon: 'fa fa-trash' }
};

function isPlainRow(row) {
  return row !== null && typeof row === 'object' && !Array.isArray(row);
}

/**
 * §12.1 → [{ key, labelKey, icon, divided }] in ROW_ACTION_KEYS order.
 * `roles` = { isAdmin, isOwner, isEmployee, isAccessPayment, isAgent }; a
 * missing flag is false. Junk / null row → []. Never mutates its input.
 */
export function rowActions(row, roles) {
  if (!isPlainRow(row)) return [];
  const r = roles && typeof roles === 'object' ? roles : {};
  const flag = k => (r[k] === undefined ? false : r[k]);
  const status = Number(row.order_status);
  const simulate = !!row.simulate_enabled && SIMULATE_STATUSES.indexOf(status) >= 0;
  const allowed = {
    view: true,
    shipping: status === 100,
    tracking: TRACKING_EDIT_STATUSES.indexOf(status) >= 0 && !!flag('isAdmin'),
    sim_delivered: simulate,
    sim_failed: simulate,
    sim_returned: simulate,
    cancel: canDeleteOrder({
      orderStatus: row.order_status,
      isAdmin: flag('isAdmin'),
      isOwner: flag('isOwner'),
      isEmployee: flag('isEmployee'),
      isAccessPayment: flag('isAccessPayment'),
      isAgent: flag('isAgent')
    })
  };
  return ROW_ACTION_KEYS.filter(k => allowed[k]).map(key => ({
    key,
    labelKey: ROW_ACTION_META[key].labelKey,
    icon: ROW_ACTION_META[key].icon,
    // A divider opens the simulate group and sits above cancel.
    divided: key === 'sim_delivered' || key === 'cancel'
  }));
}

/** Receiver-side re-check (OrderTables.vue#applyArrival). */
export function rowActionAllowed(kind, row, roles) {
  return rowActions(row, roles).some(a => a.key === kind);
}

/**
 * §12.3: the hand-off read back from `$route.params.row_action`
 * → { kind, row } | null. Only HANDOFF_KINDS; the row must be an object with
 * an ID. Never throws.
 */
export function rowActionFromRoute(route) {
  try {
    const ra = route && route.params ? route.params.row_action : undefined;
    if (!ra || typeof ra !== 'object') return null;
    if (HANDOFF_KINDS.indexOf(ra.kind) < 0) return null;
    if (!isPlainRow(ra.row) || ra.row.ID == null) return null;
    return { kind: ra.kind, row: ra.row };
  } catch (e) {
    return null;
  }
}

export default {
  TAB_KEYS,
  DEFAULT_TAB,
  TAB_STATUSES,
  ALL_STATUS_CODES,
  STATUS_LABEL_KEYS,
  ROW_CAPS,
  MAX_RANGE_DAYS,
  PAGE_SIZE,
  PAGE_SIZES,
  DEFAULT_PLATFORM,
  DATE_TYPES,
  DEFAULT_DATE_TYPE,
  PACKING_OPTIONS,
  isTabKey,
  tabOfStatus,
  subStatusOptions,
  statusBadgeClass,
  orderStatusExtras,
  countTabs,
  rowsFor,
  lastPage,
  pageOf,
  isTruncated,
  classifyKeyword,
  defaultRange,
  emptyFilters,
  normalizeFilters,
  rangeError,
  filtersFromQuery,
  queryFromFilters,
  apiParams,
  activeChips,
  showPeriodTag,
  activeCount,
  removeChip,
  platformIds,
  platformApiParams,
  normalizePlatform,
  normalizeSize,
  mergeRows,
  rowsOfPlatform,
  platformRows,
  STAGE_KEYS,
  STAGE_TAB,
  STAGE_STATUS,
  isStageKey,
  hasTimestamp,
  fulfilmentStage,
  countStages,
  rowsForStage,
  normalizeStage,
  ROW_ACTION_KEYS,
  HANDOFF_KINDS,
  TRACKING_EDIT_STATUSES,
  SIMULATE_STATUSES,
  rowActions,
  rowActionAllowed,
  rowActionFromRoute
};
